<template>
  <div class="d-flex flex-column align-items-start">
    <b-badge
      pill
      :variant="`light-${variant}`"
      class="mb-1 text-capitalize"
      v-b-tooltip.hover
      :title="tooltipMessage"
      v-if="tooltipMessage.trim().length > 0"
    >
      {{ status }}

      <feather-icon :icon="fbrInvoiceNumber ? 'InfoIcon' : 'AlertCircleIcon'" class="ml-1" />
    </b-badge>
    <b-badge pill :variant="`light-${variant}`" class="mb-1 text-capitalize" v-else>
      {{ status }}
    </b-badge>

    <b-button variant="outline-primary" @click="onClickUpload" size="sm" v-show="showUploadButton"
      >Upload</b-button
    >
  </div>
</template>

<script>
import { computed, onUnmounted, ref, watch } from '@vue/composition-api';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';

import { BBadge, BButton, VBTooltip } from 'bootstrap-vue';
import store from '@/store';
import { useToast } from 'vue-toastification/composition';
import invoiceStoreModule from '../../sales-invoice/invoiceStoreModule';
import router from '@/router';

const FBR_STATUS_MAP = {
  POSTED: 'Posted',
  INVALID: 'Invalid',
  PROCESSING: 'Processing',
  PENDING: 'Pending',
};

export default {
  name: 'FBRStatusCell',
  components: {
    BBadge,
    BButton,
    ToastificationContent,
    ToastificationContent,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    data: {
      type: Object,
      required: true,
    },
  },
  emits: ['uploaded'],
  setup(props) {
    const toast = useToast();

    const INVOICE_APP_STORE_MODULE_NAME = 'sales-invoice';

    // Register module
    if (!store.hasModule(INVOICE_APP_STORE_MODULE_NAME))
      store.registerModule(INVOICE_APP_STORE_MODULE_NAME, invoiceStoreModule);

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(INVOICE_APP_STORE_MODULE_NAME))
        store.unregisterModule(INVOICE_APP_STORE_MODULE_NAME);
    });

    const { data } = props;
    const { id, fbrStatus, fbrNote, fbrInvoiceNo } = data;

    const getStatus = (status) => {
      switch (status) {
        case 'posted':
          return FBR_STATUS_MAP.POSTED;

        case 'Invalid':
          return FBR_STATUS_MAP.INVALID;

        case 'processing':
          return FBR_STATUS_MAP.PROCESSING;

        default:
          return FBR_STATUS_MAP.PENDING;
      }
    };

    const getVariant = (status) => {
      switch (status) {
        case FBR_STATUS_MAP.POSTED:
          return 'success';

        case FBR_STATUS_MAP.INVALID:
          return 'danger';

        case FBR_STATUS_MAP.PROCESSING:
          return 'info';

        default:
          return 'warning';
      }
    };

    const status = ref(getStatus(fbrStatus));
    const variant = computed(() => getVariant(status.value));

    const errorMessage = ref(fbrNote || '');
    const fbrInvoiceNumber = ref(fbrInvoiceNo || '');

    const tooltipMessage = computed(() => {
      if (fbrInvoiceNumber.value) {
        return fbrInvoiceNumber.value;
      }
      return errorMessage.value || '';
    });

    const showUploadButton = computed(() => {
      return [FBR_STATUS_MAP.PENDING, FBR_STATUS_MAP.INVALID].includes(status.value);
    });

    const isUploaded = ref(false);

    // Watch for changes in the entire data object to maintain reactivity
    watch(
      () => props.data,
      (newData) => {
        if (newData) {
          status.value = getStatus(newData.fbrStatus);
          errorMessage.value = newData.fbrNote || '';
          fbrInvoiceNumber.value = newData.fbrInvoiceNo || '';
        }
      },
      { deep: true, immediate: true }
    );

    const onClickUpload = () => {
      errorMessage.value = '';
      status.value = FBR_STATUS_MAP.PROCESSING;

      store
        .dispatch('sales-invoice/UploadToFBR', [props.data.id])
        .then((response) => {
          console.log('Response:', response);
          
          const results = response.data || response;
          const individualResult = results.uploadResults ? results.uploadResults[0] : null;

          if (results.status === 'success' || (individualResult && individualResult.status === 'success')) {
            status.value = FBR_STATUS_MAP.POSTED;
            toast({
              component: ToastificationContent,
              props: {
                title: 'Invoice uploaded successfully',
                icon: 'CheckCircleIcon',
                variant: 'success',
                text: results.message || 'The invoice was successfully uploaded to FBR.',
              },
            });
            // Fetch updated list to ensure state is in sync
            router.go(0); // Simple refresh or emit event
          } else {
            status.value = FBR_STATUS_MAP.INVALID;
            errorMessage.value = individualResult ? individualResult.message : results.message;
            toast({
              component: ToastificationContent,
              props: {
                title: 'Upload Failed',
                icon: 'AlertCircleIcon',
                variant: 'danger',
                text: errorMessage.value,
              },
            });
          }
        })
        .catch((error) => {
          status.value = FBR_STATUS_MAP.INVALID;
          console.error('Upload error:', error);

          errorMessage.value = error.message || 'An unexpected error occurred';

          toast({
            component: ToastificationContent,
            props: {
              title: 'Failed to upload invoice',
              icon: 'AlertCircleIcon',
              variant: 'danger',
              text: errorMessage.value,
            },
          });
        });
    };

    return {
      fbrStatus,
      isUploaded,
      status,
      variant,
      onClickUpload,
      showUploadButton,
      errorMessage,
      fbrInvoiceNumber,
      tooltipMessage,
    };
  },
};
</script>
